import loadImage from 'blueimp-load-image';
import { uniqueId } from 'lodash';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import Dropzone from './Dropzone';
import FilesList from './FilesList';

/**
 * This component is responsible for accepting, resizing, compressing and uploading photos
 * It is supposed to notify parent once files have been selected and selection confirmed by the user
 * This component will also generate thumbnail previews.
 * We shall still upload original picture
 * We will use https://github.com/blueimp/JavaScript-Load-Image for previewing thumbnail and interacting with EXIF data
 * This repo will also fix EXIF rotation from IOS devices
 */

interface PhotoUploaderProps {
    handleFilesChange: (files: any[], thumbs: any[]) => void;
    maxPhotos: number;
    initialFiles?: any[];
    initialThumbs?: any[];
    enableDescriptions?: boolean;
}

function PhotoUploader({
    handleFilesChange,
    maxPhotos = 10,
    initialFiles = [],
    initialThumbs = [],
    enableDescriptions = false
}: PhotoUploaderProps) {
    const [files, setFiles] = useState(initialFiles);
    const [thumbnails, setThumbnails] = useState(initialThumbs);

    useEffect(() => {
        handleFilesChange(files, thumbnails);
    }, [files, handleFilesChange, thumbnails]);

    const handleAcceptedFiles = (acceptedFiles) => {
        // Dropzone has accepted image files which are not larger then specified size
        // We should resize and compress this images

        acceptedFiles = acceptedFiles.map((file) => {
            file.id = uniqueId();
            return file;
        });

        if (maxPhotos > 1) {
            const remainingFiles = maxPhotos - (files.length + acceptedFiles.length);

            if (remainingFiles < 0) {
                toast(`Nekaj datotek ni bilo naloženih. Največje število datotek: ${maxPhotos}`, {
                    icon: 'ℹ️'
                });
                // Take only remaining files from acceptedFiles
                const canUpload = maxPhotos - files.length;
                acceptedFiles = acceptedFiles.splice(0, canUpload);
            }
            setFiles((files) => [...files, ...acceptedFiles]);
        } else {
            setFiles([...acceptedFiles]);
        }

        for (let i = 0; i < acceptedFiles.length; i++) {
            const uploadedFile = acceptedFiles[i];
            loadImage(uploadedFile, { maxWidth: 200, orientation: true, canvas: true }).then((data) => {
                onThumbnailGenerated(data.image.toDataURL());
            });
        }
    };

    const onThumbnailGenerated = (blob) => {
        if (maxPhotos === 1) {
            setThumbnails([blob]);
            return;
        }
        setThumbnails((thumbs) => [...thumbs, blob]);
    };

    const handleFileRemove = (file, i) => {
        setFiles((files) => files.filter((file, index) => i !== index));
        setThumbnails((thumbs) => thumbs.filter((thumbs, index) => i !== index));
    };

    const handleFileDescriptionChange = (description, index) => {
        const updatedFiles = [...files];
        updatedFiles[index].description = description;
        setFiles(updatedFiles);
    };

    return (
        <>
            <Dropzone
                onAcceptedFiles={handleAcceptedFiles}
                accept={['image/*']}
                multiple={maxPhotos > 1}
                maxSize={12}
                maxFiles={maxPhotos}
            />

            {files.length > 0 && (
                <FilesList
                    files={files}
                    thumbnails={thumbnails}
                    onFileRemove={handleFileRemove}
                    enableDescriptions={enableDescriptions}
                    onDescriptionChange={handleFileDescriptionChange}
                />
            )}
        </>
    );
}

export default PhotoUploader;
